import { AntragDtoModel, AntragsInfoExtended } from '../models';
import { AntragHelperService } from './antrag-helper.service';
import { ChangeDetectionStrategy, Component, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { MessageBase, ResourceProvider } from '../providers';
import { TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { firstValueFrom } from 'rxjs';
import { sortArrayByAttribute } from '../helpers';

@Component({
  selector: 'ebeihilfe-antrags-overview',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class AntragsOverviewBaseComponent implements OnInit {
  public antragsList: WritableSignal<AntragsInfoExtended[]> = signal<AntragsInfoExtended[]>([]);
  public drafts: WritableSignal<AntragsInfoExtended[]> = signal<AntragsInfoExtended[]>([]);
  public isLoadingAntrage = signal(false);
  public orderByArray: { value: keyof AntragsInfoExtended; text: string }[] | undefined;

  protected readonly antragHelper = inject(AntragHelperService);
  protected readonly message = inject(MessageBase);
  protected readonly resource = inject(ResourceProvider);
  protected readonly translate = inject(TranslateService);

  public async loadAntraege(): Promise<void> {
    return this.loadAntraegeInternal();
  }

  public ngOnInit(): void {
    this.loadAntraege();

    this.orderByArray = [
      {
        value: 'hochgeladen',
        text: this.translate.instant(_('bescheid_list.label.orderBy.angelegt_am')),
      },
      {
        value: 'anzBelege',
        text: this.translate.instant(_('bescheid_list.label.orderBy.anzahl_belege')),
      },
      {
        value: 'anzSeiten',
        text: this.translate.instant(_('bescheid_list.label.orderBy.anzahl_seiten')),
      },
      {
        value: 'status',
        text: this.translate.instant(_('bescheid_list.label.orderBy.status')),
      },
      {
        value: 'seitenBytesTotal',
        text: this.translate.instant(_('bescheid_list.label.orderBy.groesse')),
      },
    ];
  }

  public refresh(): Promise<void> {
    return this.loadAntraege();
  }

  public sortAntragsList(nameOfAttribute: keyof AntragsInfoExtended): void {
    const sortByAttributeType = nameOfAttribute === 'hochgeladen' ? 'date' : 'number';
    sortArrayByAttribute(this.antragsList(), nameOfAttribute, sortByAttributeType);
  }

  protected abstract hideSpinner(): void | Promise<boolean>;

  protected abstract showSpinner(): void | Promise<void>;

  protected async loadAntraegeInternal(ratingFn?: () => Promise<void>): Promise<void> {
    if (this.isLoadingAntrage()) {
      return;
    }

    this.isLoadingAntrage.set(true);
    this.showSpinner();

    let infoList: AntragsInfoExtended[] = [];

    try {
      const antrage = await firstValueFrom(this.resource.getAntragsHistory(true));
      if (antrage.length > 2 && !!ratingFn) {
        await ratingFn();
      }
      infoList = await this.convertIntoExtendedAntragsInfo(antrage);
    } catch (err: unknown) {
      if (err instanceof Error) {
        await this.message.showError(err);
      }
    }

    if (infoList) {
      this.antragsList.set(
        sortArrayByAttribute(
          infoList.filter((antrag) => antrag.hochgeladen !== null),
          'hochgeladen',
          'date',
        ),
      );
      this.drafts.set(
        sortArrayByAttribute(
          infoList.filter((antrag) => antrag.hochgeladen === null),
          'angelegt',
          'date',
        ),
      );
    }

    this.hideSpinner();
    this.isLoadingAntrage.set(false);
  }

  private async convertIntoExtendedAntragsInfo(antrage: AntragDtoModel[]): Promise<AntragsInfoExtended[]> {
    const filteredAntraege = antrage.filter(
      (antrag) => !this.antragHelper.filterForDifferentEinreichungsVersions(antrag),
    );

    return await Promise.all(
      filteredAntraege.map(async (antrag) => await this.antragHelper.turnIntoAntragsInfoExtended(antrag)),
    );
  }
}
